/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Canvas, Textbox, cache } from 'fabric';

import { isSafari } from '@app/app/lib/getIsSafari';

import { TextEntity } from '../../metaBullets/classes/TextEntity';

/**
 * Правильно пересчитывает размеры текстового объекта на основе его содержимого
 * Использует calcTextWidth() и calcTextHeight() для точного определения размеров
 *
 * ПРИМЕНЯЕТСЯ:
 * - При изменении стилей текста (жирность, подчеркивание)
 * - При изменении размера шрифта
 * - При загрузке текстовых объектов на канвас
 * - После выхода из режима редактирования текста
 * - При загрузке шрифтов
 *
 * РЕШАЕТ ПРОБЛЕМЫ:
 * - Текст выходит за границы текстовой коробки
 * - Рамка выделения не соответствует фактическому размеру текста
 * - Обрезание текста при загрузке канваса
 *
 * ЛОГИКА:
 * - Увеличивает размеры коробки если текст не помещается
 * - Сохраняет размеры коробки если в ней есть дополнительное пространство
 *
 * @param textObject - Текстовый объект для обновления
 * @param canvas - Canvas для принудительного рендеринга
 * @param forceResize - Принудительно изменить размеры даже если коробка больше текста
 */
export const recalculateTextboxDimensions = (
  textObject: Textbox | TextEntity,
  canvas?: Canvas,
  forceResize: boolean = false,
) => {
  try {
    // Сохраняем текущие позиции и параметры якорей
    const originalLeft = textObject.left || 0;
    const originalTop = textObject.top || 0;
    const originX = textObject.originX || 'left';
    const originY = textObject.originY || 'top';
    const originalWidth = textObject.width || 0;
    const originalHeight = textObject.height || 0;

    // Очищаем все кэши
    if (typeof (textObject as any)._forceClearCache === 'function') {
      (textObject as any)._forceClearCache();
    }
    textObject._clearCache();

    // Принудительно помечаем объект как "грязный"
    textObject.dirty = true;

    // Сбрасываем кэшированные размеры
    (textObject as any)._textBeforeEdit = undefined;
    (textObject as any).__lineWidths = undefined;
    (textObject as any).__lineHeights = undefined;

    // Пересчитываем размеры объекта
    if (typeof (textObject as any).initDimensions === 'function') {
      (textObject as any).initDimensions();
    }

    // Получаем минимально необходимые размеры для текста
    let requiredWidth = 0;
    let requiredHeight = 0;

    try {
      // Используем методы Fabric.js для вычисления размеров
      if (typeof textObject.calcTextWidth === 'function') {
        requiredWidth = textObject.calcTextWidth();
      }
      if (typeof textObject.calcTextHeight === 'function') {
        requiredHeight = textObject.calcTextHeight();
      }

      // Если размеры не были вычислены, используем текущие размеры
      if (requiredWidth <= 0) {
        requiredWidth = textObject.width || 0;
      }
      if (requiredHeight <= 0) {
        requiredHeight = textObject.height || 0;
      }
    } catch (error) {
      console.warn('Error calculating text dimensions:', error);
      // В случае ошибки используем стандартные размеры
      requiredWidth = textObject.width || 0;
      requiredHeight = textObject.height || 0;
    }

    // Определяем финальные размеры коробки
    let finalWidth = originalWidth;
    let finalHeight = originalHeight;

    if (forceResize) {
      // Принудительно устанавливаем размеры под текст
      finalWidth = requiredWidth;
      finalHeight = requiredHeight;
    } else {
      // Увеличиваем размеры только если текст не помещается
      finalWidth = Math.max(originalWidth, requiredWidth);
      finalHeight = Math.max(originalHeight, requiredHeight);
    }

    // Устанавливаем новые размеры только если они изменились
    if (finalWidth !== originalWidth || finalHeight !== originalHeight) {
      textObject.set({
        width: finalWidth,
        height: finalHeight,
      });

      // Вычисляем координаты левого верхнего угла для исходных размеров
      let topLeftCornerX = originalLeft;
      let topLeftCornerY = originalTop;

      if (originX === 'center') {
        topLeftCornerX -= originalWidth / 2;
      } else if (originX === 'right') {
        topLeftCornerX -= originalWidth;
      }

      if (originY === 'center') {
        topLeftCornerY -= originalHeight / 2;
      } else if (originY === 'bottom') {
        topLeftCornerY -= originalHeight;
      }

      // Вычисляем новую позицию для сохранения положения левого верхнего угла
      let newLeft = topLeftCornerX;
      let newTop = topLeftCornerY;

      if (originX === 'center') {
        newLeft += finalWidth / 2;
      } else if (originX === 'right') {
        newLeft += finalWidth;
      }

      if (originY === 'center') {
        newTop += finalHeight / 2;
      } else if (originY === 'bottom') {
        newTop += finalHeight;
      }

      // Применяем новую позицию
      textObject.set({
        left: newLeft,
        top: newTop,
      });
    }

    // Обновляем координаты объекта
    textObject.setCoords();

    // Дополнительно для Safari: небольшая задержка и повторное обновление
    if (isSafari()) {
      requestAnimationFrame(() => {
        textObject._clearCache();
        textObject.setCoords();
        if (canvas) {
          canvas.requestRenderAll();
        }
      });
    }

    // Принудительно перерисовываем canvas если он передан
    if (canvas) {
      canvas.requestRenderAll();
    }
  } catch (error) {
    console.warn('Error in recalculateTextboxDimensions:', error);
  }
};

/**
 * Принудительно обновляет границы текстового объекта
 * Решает проблему с неправильными размерами рамки после изменения стилей
 *
 * ПРИМЕНЯЕТСЯ:
 * - Как обертка над recalculateTextboxDimensions для обратной совместимости
 * - В местах где нужно быстро исправить границы текста
 *
 * @param textObject - Текстовый объект для обновления
 * @param canvas - Canvas для принудительного рендеринга
 */
export const forceTextBoundingBoxUpdate = (textObject: Textbox | TextEntity, canvas?: Canvas) => {
  console.warn('forceTextBoundingBoxUpdate is deprecated, use recalculateTextboxDimensions');
  // Используем новую функцию с флагом принудительного изменения размера
  recalculateTextboxDimensions(textObject, canvas);
};

/**
 * Массово исправляет границы всех текстовых объектов на канвасе
 * Особенно полезно после загрузки шрифтов или изменения масштаба
 * @param canvas - Canvas для обновления
 * @param delay - Задержка перед обновлением (полезно для Safari)
 * @param forceResize - Принудительно подогнать размеры коробок под текст
 */
export const fixAllTextBoundingBoxes = async (
  canvas: Canvas,
  delay: number = 0,
  forceResize: boolean = false,
): Promise<void> => {
  return new Promise((resolve) => {
    const updateTextBoxes = () => {
      try {
        // Находим все текстовые объекты на canvas
        const textObjects = canvas
          .getObjects()
          .filter((obj) => obj.type === 'textbox' || obj.type === 'textentity') as (
          | Textbox
          | TextEntity
        )[];

        if (textObjects.length === 0) {
          resolve();
          return;
        }

        // Сначала очищаем кэш у всех текстовых объектов
        textObjects.forEach((textObject) => {
          // Дополнительная очистка кэша и установка dirty флага
          if (typeof (textObject as any)._forceClearCache === 'function') {
            (textObject as any)._forceClearCache();
          }
          textObject._clearCache();
          textObject.dirty = true;
        });

        // Затем обновляем каждый текстовый объект
        textObjects.forEach((textObject) => {
          recalculateTextboxDimensions(textObject, canvas, forceResize);
        });

        // Дополнительная задержка для Safari
        if (isSafari()) {
          setTimeout(() => {
            textObjects.forEach((textObject) => {
              textObject.setCoords();
            });
            canvas.requestRenderAll();
            resolve();
          }, 100);
        } else {
          canvas.requestRenderAll();
          resolve();
        }
      } catch (error) {
        console.warn('Error in fixAllTextBoundingBoxes:', error);
        resolve();
      }
    };

    if (delay > 0) {
      setTimeout(updateTextBoxes, delay);
    } else {
      updateTextBoxes();
    }
  });
};

/**
 * Обновляет шрифты всех текстовых объектов на canvas
 * Очищает кэш, пересчитывает размеры и обновляет координаты
 * @param canvas - Canvas или StaticCanvas для обновления
 * @param options - Дополнительные опции
 */
export const refreshTextObjectsFonts = async (
  canvas: Canvas | import('fabric').StaticCanvas,
  options: {
    waitForFonts?: boolean; // Ждать готовности шрифтов перед обновлением
    logResults?: boolean; // Логировать результаты
  } = {},
): Promise<void> => {
  const { waitForFonts = true, logResults = true } = options;

  try {
    // Ждем готовности шрифтов если указано
    if (waitForFonts) {
      await document.fonts.ready;
    }

    // Получаем все текстовые объекты
    const allTextObjects = canvas
      .getObjects()
      .filter(
        (obj: any) =>
          obj.type === 'textbox' ||
          obj.type === 'i-text' ||
          obj.type === 'text' ||
          obj.type === 'textentity',
      );

    if (allTextObjects.length === 0) {
      if (logResults) {
        // console.debug('No text objects found to refresh fonts');
      }
      return;
    }

    // Обновляем каждый текстовый объект
    allTextObjects.forEach((obj) => {
      try {
        if (obj instanceof Textbox) {
          // Используем функцию для правильного пересчета размеров (только увеличение при необходимости)
          recalculateTextboxDimensions(obj, canvas as Canvas);
        } else {
          // Для других типов текстовых объектов используем стандартный подход
          // Очищаем кэш текста для правильного рендеринга шрифтов
          if (typeof (obj as any)._forceClearCache === 'function') {
            (obj as any)._forceClearCache();
          }

          // Пересчитываем размеры с новым шрифтом
          if (typeof (obj as any).initDimensions === 'function') {
            (obj as any).initDimensions();
          }

          // Обновляем координаты объекта
          obj.setCoords();
        }
      } catch (error) {
        console.warn(`Error refreshing font for text object ${obj.id}:`, error);
      }
    });

    // Принудительно перерисовываем canvas
    canvas.renderAll();

    if (logResults) {
      // console.debug(`✅ Font refresh applied to ${allTextObjects.length} text objects`);
    }
  } catch (error) {
    console.warn('Error in refreshTextObjectsFonts:', error);
  }
};

/**
 * Устанавливает слушатель изменений шрифтов и автоматически обновляет текстовые объекты
 * @param canvas - Canvas для мониторинга
 * @returns Функция для отключения мониторинга
 */
export const setupFontChangeWatcher = (canvas: Canvas): (() => void) => {
  let isWatching = true;

  const handleFontChange = async () => {
    if (!isWatching) return;

    try {
      // Ждем готовности всех шрифтов
      await document.fonts.ready;

      // Небольшая задержка для стабилизации
      setTimeout(async () => {
        if (isWatching && canvas) {
          await fixAllTextBoundingBoxes(canvas, 50);
        }
      }, 100);
    } catch (error) {
      console.warn('Error in font change watcher:', error);
    }
  };

  // Слушаем события загрузки шрифтов
  if (document.fonts && typeof document.fonts.addEventListener === 'function') {
    document.fonts.addEventListener('loadingdone', handleFontChange);
    document.fonts.addEventListener('loadingerror', handleFontChange);
  }

  // Функция для отключения мониторинга
  return () => {
    isWatching = false;
    if (document.fonts && typeof document.fonts.removeEventListener === 'function') {
      document.fonts.removeEventListener('loadingdone', handleFontChange);
      document.fonts.removeEventListener('loadingerror', handleFontChange);
    }
  };
};

/**
 * Обрабатывает все текстовые объекты при первоначальной загрузке канваса
 * Применяет правильный пересчет размеров для всех текстовых коробок
 * @param canvas - Canvas для обработки
 * @param options - Дополнительные опции
 */
export const processTextObjectsOnLoad = async (
  canvas: Canvas | import('fabric').StaticCanvas,
  options: {
    waitForFonts?: boolean; // Ждать готовности шрифтов
    fixDimensions?: boolean; // Исправлять размеры текстовых коробок
    logResults?: boolean; // Логировать результаты
  } = {},
): Promise<void> => {
  const { waitForFonts = true, fixDimensions = true, logResults = false } = options;

  try {
    // Ждем готовности шрифтов если указано
    if (waitForFonts) {
      await document.fonts.ready;

      // Дополнительная небольшая задержка для Safari
      if (isSafari()) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Принудительно очищаем кэш Fabric.js
    clearFabricFontCache(canvas as Canvas);

    // Получаем все текстовые объекты
    const allTextObjects = (canvas as any)
      .getObjects()
      .filter(
        (obj: any) =>
          obj.type === 'textbox' ||
          obj.type === 'i-text' ||
          obj.type === 'text' ||
          obj.type === 'textentity',
      );

    if (allTextObjects.length === 0) {
      if (logResults) {
        console.debug('No textbox objects found to process on load');
      }
      return;
    }

    if (logResults) {
      console.debug(`Processing ${allTextObjects.length} textbox objects on canvas load`);
    }

    // Обрабатываем каждый текстовый объект
    for (const textObject of allTextObjects) {
      try {
        // Устанавливаем originalText если он не задан
        if (textObject.originalText === undefined || textObject.originalText === null) {
          textObject.set('originalText', textObject.text);
        }

        // Принудительно очищаем кэш объекта
        if (typeof (textObject as any)._forceClearCache === 'function') {
          (textObject as any)._forceClearCache();
        }
        textObject._clearCache();
        textObject.dirty = true;

        // Исправляем размеры если необходимо (только увеличение при необходимости)
        if (fixDimensions) {
          recalculateTextboxDimensions(textObject as Textbox, canvas as Canvas);
        } else {
          // Только обновляем координаты
          textObject.setCoords();
        }
      } catch (error) {
        console.warn(`Error processing textbox object ${textObject.id} on load:`, error);
      }
    }

    // Принудительно перерисовываем canvas
    canvas.renderAll();

    if (logResults) {
      console.debug(`✅ Processed ${allTextObjects.length} textbox objects on canvas load`);
    }
  } catch (error) {
    console.warn('Error in processTextObjectsOnLoad:', error);
  }
};

/**
 * Принудительно очищает кэш шрифтов Fabric.js для указанного canvas
 * Устанавливает dirty флаг и очищает кэш у всех текстовых объектов на canvas
 * Это критично для правильной работы текстовых объектов после загрузки шрифтов
 * @param canvas - Canvas для очистки кэша текстовых объектов
 * @returns Статистика очистки
 */
export const clearFabricFontCache = (canvas: Canvas | import('fabric').StaticCanvas) => {
  try {
    // console.debug('🧹 Clearing Fabric.js font cache for canvas...');

    if (!canvas || typeof canvas.getObjects !== 'function') {
      console.warn('Invalid canvas provided to clearFabricFontCache');
      return {
        textObjectsCleared: 0,
        canvasesProcessed: 0,
      };
    }

    cache.clearFontCache();
    cache.charWidthsCache = {};

    // Получаем все текстовые объекты на указанном canvas
    const allTextObjects = (canvas as any)
      .getObjects()
      .filter(
        (obj: any) =>
          obj.type === 'textbox' ||
          obj.type === 'i-text' ||
          obj.type === 'text' ||
          obj.type === 'textentity',
      );

    let textObjectsCleared = 0;

    // Очищаем кэш у каждого текстового объекта
    allTextObjects.forEach((textObject: any) => {
      // Устанавливаем dirty флаг - это правильный способ инвалидации кэша в Fabric.js
      textObject.dirty = true;

      // Принудительно очищаем кэш объекта
      if (typeof textObject._clearCache === 'function') {
        textObject._clearCache();
      }

      // Дополнительно очищаем внутренние кэши если они есть
      if (textObject._cacheCanvas) {
        textObject._cacheCanvas = null;
      }
      if (textObject._cacheContext) {
        textObject._cacheContext = null;
      }

      textObjectsCleared++;
    });

    // console.debug(`✅ Cleared cache for ${textObjectsCleared} text objects on canvas`);

    return {
      textObjectsCleared,
      canvasesProcessed: 1,
    };
  } catch (error) {
    console.warn('Error clearing Fabric.js font cache:', error);
    return {
      textObjectsCleared: 0,
      canvasesProcessed: 0,
    };
  }
};

/**
 * Проверяет состояние кэша текстовых объектов на указанном canvas
 * @param canvas - Canvas для проверки
 * @returns Объект с информацией о состоянии кэша
 */
export const checkFabricFontCache = (canvas: Canvas | import('fabric').StaticCanvas) => {
  try {
    if (!canvas || typeof canvas.getObjects !== 'function') {
      console.warn('Invalid canvas provided to checkFabricFontCache');
      return {
        totalTextObjects: 0,
        dirtyTextObjects: 0,
        cachedTextObjects: 0,
        cacheIsClean: false,
      };
    }

    let totalTextObjects = 0;
    let dirtyTextObjects = 0;
    let cachedTextObjects = 0;

    // Получаем все текстовые объекты на указанном canvas
    const allTextObjects = (canvas as any)
      .getObjects()
      .filter(
        (obj: any) =>
          obj.type === 'textbox' ||
          obj.type === 'i-text' ||
          obj.type === 'text' ||
          obj.type === 'textentity',
      );

    // Проверяем состояние каждого текстового объекта
    allTextObjects.forEach((textObject: any) => {
      totalTextObjects++;

      if (textObject.dirty) {
        dirtyTextObjects++;
      }

      if (textObject._cacheCanvas) {
        cachedTextObjects++;
      }
    });

    const result = {
      totalTextObjects,
      dirtyTextObjects,
      cachedTextObjects,
      cacheIsClean: dirtyTextObjects === 0,
    };

    return result;
  } catch (error) {
    console.warn('Не удалось проверить состояние кэша:', error);
    return {
      totalTextObjects: 0,
      dirtyTextObjects: 0,
      cachedTextObjects: 0,
      cacheIsClean: false,
    };
  }
};

/**
 * Ручное исправление размеров выделенных текстовых объектов
 * Эта функция может быть вызвана пользователем или по требованию
 * @param canvas - Canvas для обработки
 * @param targetIds - ID объектов для обработки (если не указаны, обрабатываются выделенные)
 * @returns Количество обработанных объектов
 */
export const manuallyFixTextDimensions = (canvas: Canvas, targetIds?: string[]): number => {
  try {
    let objectsToProcess: any[] = [];

    if (targetIds && targetIds.length > 0) {
      // Обрабатываем указанные объекты
      const allObjects = canvas.getObjects();
      objectsToProcess = allObjects.filter(
        (obj) => targetIds.includes(obj.id) && obj.type === 'textbox',
      );
    } else {
      // Обрабатываем выделенные объекты
      const activeObjects = canvas.getActiveObjects();
      objectsToProcess = activeObjects.filter(
        (obj) => obj.type === 'textbox' || obj.type === 'textentity',
      );
    }

    if (objectsToProcess.length === 0) {
      console.debug('No textbox objects found to fix dimensions');
      return 0;
    }

    // Принудительно очищаем кэш
    clearFabricFontCache(canvas as Canvas);

    // Обрабатываем каждый текстовый объект
    objectsToProcess.forEach((textObject) => {
      try {
        console.debug(`Manually fixing dimensions for textbox ${textObject.id}`);
        recalculateTextboxDimensions(textObject as Textbox, canvas);
      } catch (error) {
        console.warn(`Error fixing dimensions for textbox ${textObject.id}:`, error);
      }
    });

    canvas.requestRenderAll();

    console.debug(`✅ Manually fixed dimensions for ${objectsToProcess.length} textbox objects`);
    return objectsToProcess.length;
  } catch (error) {
    console.warn('Error in manuallyFixTextDimensions:', error);
    return 0;
  }
};

/**
 * Уведомляет canvas о готовности шрифтов и выполняет необходимые действия
 * Рекомендуется вызывать после загрузки шрифтов через FontFace API
 * @param canvas - Canvas для обработки
 * @returns Статистика обработки
 */
export const notifyCanvasAboutFontsReady = async (
  canvas: Canvas | import('fabric').StaticCanvas,
): Promise<{ textObjectsProcessed: number; success: boolean }> => {
  try {
    // 1) Проверка валидности canvas
    if (!canvas || typeof canvas.getObjects !== 'function') {
      console.warn('Invalid canvas provided to notifyCanvasAboutFontsReady');
      return { textObjectsProcessed: 0, success: false };
    }

    // 2) Обработка текстовых объектов
    // ВАЖНО: processTextObjectsOnLoad уже выполняет очистку кеша шрифтов через clearFabricFontCache
    // Поэтому дублировать очистку выше НЕ нужно — это избыточно и даёт лишний рендер.
    await processTextObjectsOnLoad(canvas, {
      waitForFonts: false, // Шрифты уже загружены к этому моменту
      fixDimensions: true, // Пересчёт размеров текстовых коробок
      logResults: true,
    });

    // 3) Массовое обновление bounding boxes, чтобы гарантировать корректные рамки
    if (canvas instanceof Canvas) {
      await fixAllTextBoundingBoxes(canvas, 50);
    }

    // Примечание по статистике: processTextObjectsOnLoad внутри себя очищает кеш и обрабатывает объекты,
    // но не возвращает количество. Если понадобится точная метрика — можно расширить сигнатуру
    // processTextObjectsOnLoad и вернуть фактическое число обработанных объектов.
    return {
      textObjectsProcessed: 0,
      success: true,
    };
  } catch (error) {
    console.warn('Error notifying canvas about fonts ready:', error);
    return { textObjectsProcessed: 0, success: false };
  }
};
